import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { commonGround as cg } from "@/content/commonGround";

const HERO_IMAGE = {
  src: "/our-work-farm.jpg",
  alt: "Aerial view of a working farm with crop rows, water and shared infrastructure",
};
const GARDEN_IMAGE = {
  src: "/about-locally-grown.jpg",
  alt: "Fresh locally grown produce at a community market stall",
};
const STEWARDSHIP_IMAGE = {
  src: "/common-ground-seeds.jpg",
  alt: "Hands holding seeds over soil, ready to plant",
};

const Para = ({ children }) => (
  <p className="mt-5 first:mt-0 text-base md:text-lg leading-relaxed text-charcoal/90">{children}</p>
);

export default function CommonGround() {
  return (
    <>
      <Seo
        title="Common Ground Farm & Coop | Active Project — Bioregional Ventures"
        description="An emerging regenerative farm and community cooperative bringing people together around productive land, local food systems, independent enterprise and shared stewardship. Visioning & development stage."
        path="/our-work/common-ground"
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-ochre-dark" data-testid="cg-status">
                {cg.status}
              </p>
              <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal" data-testid="cg-heading">
                {cg.name}
              </h1>
              <p className="mt-3 font-serif italic text-xl md:text-2xl text-ochre-dark" data-testid="cg-tagline">
                {cg.tagline}
              </p>
              {cg.overview.map((p) => (
                <Para key={p.slice(0, 24)}>{p}</Para>
              ))}
            </Reveal>
          </div>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="overflow-hidden rounded-sm">
              <img
                src={HERO_IMAGE.src}
                alt={HERO_IMAGE.alt}
                data-testid="cg-hero-image"
                className="w-full h-72 md:h-[480px] object-cover hover:scale-[1.02] transition-transform duration-500"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Participation - consolidated membership explanation */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-participation-heading">
            {cg.participationHeading}
          </h2>
          <Para>{cg.participationBody}</Para>
        </Reveal>
      </section>

      {/* Community market garden — prominent visual placement */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
            <Reveal className="md:col-span-6">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={GARDEN_IMAGE.src}
                  alt={GARDEN_IMAGE.alt}
                  data-testid="cg-garden-image"
                  loading="lazy"
                  className="w-full h-80 md:h-[520px] object-cover hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
            </Reveal>
            <div className="md:col-span-6">
              <Reveal>
                <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-garden-heading">
                  {cg.gardenHeading}
                </h2>
                {cg.gardenBody.map((p) => (
                  <p key={p.slice(0, 24)} className="mt-5 text-base leading-relaxed text-charcoal/90">{p}</p>
                ))}
                <p className="mt-8 text-xs uppercase tracking-[0.15em] text-ochre-dark">{cg.gardenActivitiesLabel}</p>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm md:text-base leading-relaxed text-charcoal/85" data-testid="cg-garden-activities">
                  {cg.gardenActivities.map((a) => (
                    <li key={a} className="flex gap-3">
                      <span aria-hidden="true" className="text-ochre shrink-0">—</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Independent enterprises and shared infrastructure */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-enterprises-heading">
            {cg.enterprisesHeading}
          </h2>
          {cg.enterprisesBody.map((p) => (
            <Para key={p.slice(0, 24)}>{p}</Para>
          ))}
          <p className="mt-8 text-xs uppercase tracking-[0.15em] text-ochre-dark">{cg.infraLabel}</p>
          <ul className="mt-3 space-y-2.5 text-base leading-relaxed text-charcoal/90 list-disc pl-5" data-testid="cg-infrastructure-list">
            {cg.infrastructure.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
          <p className="mt-6 text-base leading-relaxed text-charcoal/90 border-l-2 border-ochre pl-4">{cg.infraNote}</p>
        </Reveal>
      </section>

      {/* How it fits together — simple relationship diagram */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <Reveal>
          <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-snug text-charcoal" data-testid="cg-diagram-title">
            {cg.diagramTitle}
          </h3>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cg-diagram">
          {cg.diagramNodes.map((n, i) => (
            <div key={n.title} className="relative border border-charcoal/15 bg-cream p-6" data-testid={`cg-diagram-node-${i + 1}`}>
              <span className="font-serif text-ochre">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 font-medium text-charcoal">{n.title}</p>
              {i < cg.diagramNodes.length - 1 && (
                <span aria-hidden="true" className="hidden lg:block absolute top-1/2 -right-3.5 -translate-y-1/2 text-ochre text-lg z-10">
                  →
                </span>
              )}
            </div>
          ))}
        </div>
        {cg.diagramNote && (
          <Reveal>
            <p className="mt-4 text-xs text-charcoal/60 italic">{cg.diagramNote}</p>
          </Reveal>
        )}
      </section>

      {/* Regenerative land stewardship */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-stewardship-heading">
                {cg.stewardshipHeading}
              </h2>
              {cg.stewardshipBody.map((p) => (
                <Para key={p.slice(0, 24)}>{p}</Para>
              ))}
            </Reveal>
          </div>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="overflow-hidden rounded-sm">
              <img
                src={STEWARDSHIP_IMAGE.src}
                alt={STEWARDSHIP_IMAGE.alt}
                data-testid="cg-stewardship-image"
                loading="lazy"
                className="w-full h-64 md:h-96 object-cover hover:scale-[1.02] transition-transform duration-500"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Cooperative ownership and governance */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-governance-heading">
            {cg.governanceHeading}
          </h2>
          {cg.governanceBody.map((p) => (
            <Para key={p.slice(0, 24)}>{p}</Para>
          ))}
        </Reveal>
      </section>

      {/* The vision */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug" data-testid="cg-vision-heading">
              {cg.visionHeading}
            </h2>
            <p className="mt-10 font-serif text-2xl md:text-3xl italic text-ochre" data-testid="cg-vision-statement">
              {cg.visionStatement}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Current development */}
      <section className="bg-sage/60 border-b border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-development-heading">
              {cg.developmentHeading}
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">{cg.developmentBody}</p>
            <p className="mt-8 text-xs uppercase tracking-[0.15em] text-ochre-dark">{cg.developmentLabel}</p>
            <ul className="mt-3 space-y-2.5 text-base leading-relaxed text-charcoal/90 list-disc pl-5" data-testid="cg-development-areas">
              {cg.developmentAreas.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Call to action */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cg-cta-heading">
            {cg.ctaHeading}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl mx-auto">{cg.ctaBody}</p>
          <Link
            to={cg.ctaHref}
            data-testid="cg-cta-button"
            className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {cg.ctaLabel}
          </Link>
        </Reveal>
      </section>
    </>
  );
}
